import {useEffect, useState} from 'react';

/**
 * Tracks whether a UI element has been permanently dismissed via localStorage.
 *
 * On mount, reads the stored value so the element is already hidden on the first
 * client-side render (avoids a flash of visible content after hydration).
 *
 * Returns a tuple of [isDismissed, dismiss] analogous to useState, making it a
 * drop-in replacement for the inline state + effect + handler pattern.
 *
 * @example
 * const [isBannerDismissed, dismissBanner] = useLocalStorageDismiss('my_banner_dismissed');
 */
export function useLocalStorageDismiss(storageKey: string): [boolean, () => void] {
    const [isDismissed, setIsDismissed] = useState(false);

    useEffect(() => {
        setIsDismissed(localStorage.getItem(storageKey) === 'true');
    }, [storageKey]);

    const dismiss = () => {
        localStorage.setItem(storageKey, 'true');
        setIsDismissed(true);
    };

    return [isDismissed, dismiss];
}
